import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    notification,
    Row,
    Col,
    Avatar,
    Tag,
    Popconfirm
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    SearchOutlined,
    FileTextOutlined,
    EyeOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { salesService } from '../../services/salesService';
import SalesForm from './SalesForm';

const { Column } = Table;
const { Search } = Input;

const SalesList = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const fetchInvoices = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await salesService.getAllSalesInvoices({
                page,
                limit: pagination.pageSize,
                search
            });

            setInvoices(Array.isArray(response?.data) ? response.data : []);

            setPagination(prev => ({
                ...prev,
                current: response?.pagination?.page || 1,
                total: response?.pagination?.total || 0
            }));
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch sales invoices'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    const handleTableChange = (pagination) => {
        fetchInvoices(pagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchInvoices(1, value);
    };

    const handleAdd = () => {
        setSelectedInvoice(null);
        setIsDrawerVisible(true);
    };

    const handleView = (id) => {
        navigate(`/sales/${id}`);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await salesService.deleteSalesInvoice(id);
            notification.success({
                message: 'Deleted',
                description: 'Invoice deleted successfully'
            });
            fetchInvoices(pagination.current, searchText);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || 'Failed to delete invoice'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerVisible(false);
        setSelectedInvoice(null);
    };

    const handleSuccess = () => {
        handleDrawerClose();
        fetchInvoices(pagination.current, searchText);
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space>
                                <Avatar size="large" icon={<FileTextOutlined />} style={{ backgroundColor: '#52c41a' }} />
                                <div>
                                    <h3 style={{ margin: 0 }}>Sales Invoices</h3>
                                    <p style={{ margin: 0, color: '#8c8c8c' }}>Manage customer invoices</p>
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                <Search
                                    placeholder="Search by invoice or customer"
                                    allowClear
                                    enterButton={<SearchOutlined />}
                                    size="large"
                                    style={{ width: 300 }}
                                    onSearch={handleSearch}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size="large"
                                    onClick={handleAdd}
                                >
                                    New Invoice
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Table
                        dataSource={invoices}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} invoices`
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                    >
                        <Column
                            title="Invoice Number"
                            dataIndex="invoiceNumber"
                            key="invoiceNumber"
                            render={(text) => <strong>{text}</strong>}
                        />
                        <Column
                            title="PO Number"
                            dataIndex="poNumber"
                            key="poNumber"
                            render={(text) => text || '-'}
                        />
                        <Column
                            title="Date"
                            dataIndex="invoiceDate"
                            key="invoiceDate"
                            render={(date) => new Date(date).toLocaleDateString()}
                        />
                        <Column
                            title="Customer"
                            dataIndex={['receiver', 'customerName']}
                            key="customerName"
                        />
                        <Column
                            title="Issued By"
                            dataIndex={['company', 'companyName']}
                            key="companyName"
                            render={(text) => <Tag color="blue">{text}</Tag>}
                        />
                        <Column
                            title="Contract Amt"
                            dataIndex="contractAmount"
                            key="contractAmount"
                            render={(amount) => `₹${Math.round(amount || 0)}`}
                        />
                        <Column
                            title="Total"
                            dataIndex="totalAfterTax"
                            key="totalAfterTax"
                            render={(amount) => (
                                <strong style={{ color: '#52c41a' }}>
                                    ₹{Math.round(amount || 0)}
                                </strong>
                            )}
                        />
                        <Column
                            title="Actions"
                            key="actions"
                            fixed="right"
                            width={100}
                            render={(_, record) => (
                                <Space size="middle">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => handleView(record.id)}
                                    >
                                        View
                                    </Button>
                                    <Popconfirm
                                        title="Delete Invoice"
                                        description="Are you sure you want to delete this invoice? This action cannot be undone."
                                        onConfirm={() => handleDelete(record.id)}
                                        okText="Yes, Delete"
                                        cancelText="No"
                                        okButtonProps={{ danger: true, loading: loading }}
                                    >
                                        <Button
                                            type="link"
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                        >
                                            Delete
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            )}
                        />
                    </Table>
                </Space>
            </Card>

            <Drawer
                title="Create Sales Invoice"
                width={900}
                onClose={handleDrawerClose}
                open={isDrawerVisible}
                destroyOnClose
            >
                <SalesForm
                    onSuccess={handleSuccess}
                    onCancel={handleDrawerClose}
                />
            </Drawer>
        </Space>
    );
};

export default SalesList;
